import type { OrganizationInitStatus } from '@travel-planner/shared/schemas';
import { useInitializeOrganization } from '@auth/entities/organization-init';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle2, Loader2, XCircle } from 'lucide-react';
import { Button, Modal, ModalFooter, ModalHeader, Text } from '@/shared/ui';
import css from './organization-init-dialog.module.scss';

type Props = {
  open: boolean;
  onClose: () => void;
  status: OrganizationInitStatus | null;
};

// camelCase entity key → human label for the modal lists (legacy
// `prettifyKey`). Keys come from the result's `created`/`alreadyExisted`.
const LABELS: Record<string, string> = {
  layoutTemplate: 'Demo Layout Template',
  customer: 'Demo Customer',
  product: 'Demo Product',
  itinerary: 'Demo Itinerary',
};
const prettifyKey = (key: string): string =>
  LABELS[key] ??
  key.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase());

// The items that will be created, derived from the status `has*` flags
// (legacy preview list). Only the four core entities are listed.
const missingItems = (status: OrganizationInitStatus | null): string[] => {
  if (!status) return [];
  const items: string[] = [];
  if (!status.hasLayoutTemplate) items.push('layoutTemplate');
  if (!status.hasCustomer) items.push('customer');
  if (!status.hasProduct) items.push('product');
  if (!status.hasItinerary) items.push('itinerary');
  return items;
};

export const OrganizationInitDialog = ({ open, onClose, status }: Props) => {
  const navigate = useNavigate();
  const mutation = useInitializeOrganization();
  const result = mutation.data ?? null;
  const isPending = mutation.isPending;

  const handleClose = () => {
    if (isPending) return;
    mutation.reset();
    onClose();
  };

  const handleInitialize = () => mutation.mutate();

  const handleViewItinerary = () => {
    if (!result?.itinerary) return;
    handleClose();
    navigate({
      to: '/itineraries/$itineraryId',
      params: { itineraryId: result.itinerary.id },
    });
  };

  const preview = missingItems(status);

  return (
    <Modal onClose={handleClose} open={open}>
      <ModalHeader onClose={handleClose} title="Initialize Organization" />

      {result ? (
        <div className={css.body}>
          <Text size="sm" variant="muted">
            Your organization has been set up.
          </Text>
          {result.created.length > 0 && (
            <div className={css.group}>
              <Text size="sm" weight="medium">
                Created:
              </Text>
              <ul className={css.list}>
                {result.created.map((key) => (
                  <li className={css.item} key={key}>
                    <CheckCircle2 className={css.iconCreated} />
                    <Text as="span" size="sm">
                      {prettifyKey(key)}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.alreadyExisted.length > 0 && (
            <div className={css.group}>
              <Text size="sm" weight="medium">
                Already existed:
              </Text>
              <ul className={css.list}>
                {result.alreadyExisted.map((key) => (
                  <li className={css.itemMuted} key={key}>
                    <XCircle className={css.iconExisted} />
                    <Text as="span" size="sm" variant="muted">
                      {prettifyKey(key)}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
          {result.settingsSeeded.length > 0 && (
            <div className={css.group}>
              <Text size="sm" weight="medium">
                Configuration applied (from template):
              </Text>
              <ul className={css.list}>
                {result.settingsSeeded.map((label) => (
                  <li className={css.item} key={label}>
                    <CheckCircle2 className={css.iconCreated} />
                    <Text as="span" size="sm">
                      {label}
                    </Text>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      ) : (
        <div className={css.body}>
          <Text size="sm" variant="muted">
            This will create demo content to help you get started.
          </Text>
          {isPending ? (
            <div className={css.pending}>
              <Loader2 className={css.spinner} />
              <Text size="sm" variant="muted">
                Setting up your organization…
              </Text>
            </div>
          ) : (
            <>
              {preview.length > 0 && (
                <div className={css.group}>
                  <Text size="sm" weight="medium">
                    The following items will be created:
                  </Text>
                  <ul className={css.list}>
                    {preview.map((key) => (
                      <li className={css.item} key={key}>
                        <CheckCircle2 className={css.iconCreated} />
                        <Text as="span" size="sm">
                          {prettifyKey(key)}
                        </Text>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Text className={css.note} size="sm" variant="muted">
                These demo items can be customized or deleted later.
              </Text>
            </>
          )}
          {mutation.isError && (
            <Text size="sm" variant="error">
              {mutation.error.message}
            </Text>
          )}
        </div>
      )}

      <ModalFooter>
        {result ? (
          <>
            <Button onClick={handleClose} variant="secondary">
              Close
            </Button>
            {result.itinerary && (
              <Button onClick={handleViewItinerary}>View Demo Itinerary</Button>
            )}
          </>
        ) : (
          <>
            <Button
              disabled={isPending}
              onClick={handleClose}
              variant="secondary"
            >
              Cancel
            </Button>
            <Button disabled={isPending} onClick={handleInitialize}>
              {isPending ? 'Initializing…' : 'Initialize'}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  );
};
